import './weatherdataselector.css';
import React, { useState, useEffect } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/ToastContext/ToastContext';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { ServiceNames } from '../../Api/registry/Services';

const WeatherDataSelector = ({ variables = [], latitude, longitude, onSubmit, onCancel, initialData }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [stations, setStations] = useState([]);
    const [defaultStation, setDefaultStation] = useState(null);
    const [selectedStation, setSelectedStation] = useState(initialData?.station || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectionMode, setSelectionMode] = useState(initialData?.mode || 'platform');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                if (variables.length > 0 && latitude && longitude) {
                    const modelService = await ServiceFactory.getService(ServiceNames.ServiceModel);
                    const request = {
                        latitude,
                        longitude,
                        variables
                    };

                    const stationsData = await modelService.getStations(request);
                    setStations(stationsData);

                    // Trova la prima stazione che supporta tutte le variabili richieste
                    const firstValidStation = stationsData.find(station =>
                        variables.every(variable => station.variables.includes(variable))
                    );
                    if (firstValidStation) {
                        const station = {
                            latitude: firstValidStation.latitude,
                            longitude: firstValidStation.longitude,
                            name: firstValidStation.name,
                            distance: firstValidStation.distance
                        };
                        setDefaultStation(station);
                        // Se siamo in modalità platform, impostiamo questa come stazione selezionata
                        if (selectionMode === 'platform') {
                            setSelectedStation(station);
                        }
                    }
                }
            } catch (err) {
                showToast(err.message || t("error:wrong"), "error");
                setError(t("error:loadWeatherStationFailed"));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [variables, latitude, longitude]);

    const handleStationSelect = (station) => {
        setSelectedStation({
            latitude: station.latitude,
            longitude: station.longitude,
            name: station.name,
            distance: station.distance
        });
    };

    const handleModeChange = (mode) => {
        setSelectionMode(mode);
        // Se passiamo a platform, usiamo la stazione di default
        if (mode === 'platform') {
            setSelectedStation(defaultStation);
        } else {
            setSelectedStation(null);
        }
    };

    const isConfigurationValid = () => {
        if (!variables.length) return false;
        return selectionMode === 'platform' || selectedStation !== null;
    };

    const handleConfirm = () => {
        if (isConfigurationValid()) {
            onSubmit({
                mode: selectionMode,
                station: selectedStation,
                coordinates: { latitude, longitude }
            });
        }
    };

    // Funzione di utilità per formattare la distanza
    const formatDistance = (meters) => {
        if (meters >= 1000) {
            return `${(meters / 1000).toFixed(1)} km`;
        }
        return `${Math.round(meters)} m`;
    };

    if (loading) return <div>{t("weatherdataselection:messages.loadingWeather")}</div>;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!variables.length) return <Alert variant="warning">{t("weatherdataselection:messages.noWeatherVariables")}</Alert>;
    if (!latitude || !longitude) return <Alert variant="warning">{t("weatherdataselection:messages.selectParcel")}</Alert>;

    return (
        <div className="weather-data-selector">
            <h5 className="weather-title">{t("weatherdataselection:labels.weatherDataConfig")}</h5>
            <div className="weather-data-container">
                <div className="weather-selection-row">
                    <div className="selection-options">
                        <div className="platform-option mb-3">
                            <Form.Check
                                type="radio"
                                label={t("weatherdataselection:labels.platformManaged")}
                                name="mode-selection"
                                id="platform-managed"
                                checked={selectionMode === 'platform'}
                                onChange={() => handleModeChange('platform')}
                                className="mb-2"
                            />
                            {selectionMode === 'platform' && defaultStation && (
                                <div className="default-station-info ms-4 text-muted">
                                    <small>
                                        {t("weatherdataselection:labels.usingStation")} {defaultStation.name} ({formatDistance(defaultStation.distance)})
                                    </small>
                                </div>
                            )}
                        </div>
                        <Form.Check
                            type="radio"
                            label={t("weatherdataselection:labels.manualSelection")}
                            name="mode-selection"
                            id="manual-selection"
                            checked={selectionMode === 'station'}
                            onChange={() => handleModeChange('station')}
                        />
                    </div>
                    {selectionMode === 'station' && (
                        <div className="station-selection mt-3">
                            <Form.Select
                                className="station-select"
                                value={selectedStation?.latitude || ''}
                                onChange={(e) => {
                                    const station = stations.find(s =>
                                        s.latitude === parseFloat(e.target.value)
                                    );
                                    if (station) {
                                        handleStationSelect(station);
                                    }
                                }}
                            >
                                <option value="">{t("weatherdataselection:labels.selectStation")}</option>
                                {stations
                                    .filter(station =>
                                        variables.every(variable =>
                                            station.variables.includes(variable)
                                        )
                                    )
                                    .map(station => (
                                        <option key={`${station.latitude}-${station.longitude}`} value={station.latitude}>
                                            {station.name} ({formatDistance(station.distance)})
                                        </option>
                                    ))
                                }
                            </Form.Select>
                        </div>
                    )}
                </div>
            </div>
            <div className="weather-actions">
                <Button variant="secondary" onClick={onCancel}>
                    {t("weatherdataselection:buttons.cancel")}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={!isConfigurationValid()}
                >
                    {t("weatherdataselection:buttons.confirm")}
                </Button>
            </div>
        </div>
    );
};

export default WeatherDataSelector;